/*
 * ELTANE — science page tabs
 *
 * A real tablist, not a set of anchors: roving tabindex, arrow/Home/End keys,
 * aria-selected kept in sync. Three extras beyond the ARIA pattern:
 *
 *   1. The active tab is mirrored into location.hash so a panel can be linked to
 *      and survives a reload. Written with replaceState so switching tabs does
 *      not fill the back button with history entries.
 *   2. A panel may carry a segmented group control (the Ingredient Library's
 *      Performance Gel / Pickle Juice Shot switch). Blocks tagged with a group are
 *      shown only when their group is selected.
 *   3. Stacked panels get a contents rail built from the topic headings that
 *      were actually rendered, with the entry for the topic currently on screen
 *      marked. Panels here run five or six screens deep; without it there is no
 *      way to see what a panel contains, or where in it you are.
 *
 * Rendered markup already has the first tab active, so the page is correct
 * before this runs and stays usable if it never does.
 */
class EltaneScienceTabs extends HTMLElement {
  connectedCallback() {
    this.tablist = this.querySelector('[role="tablist"]');
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
    if (!this.tabs.length) return;

    this.tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => this.select(i, { focus: false }));
      tab.addEventListener('keydown', (e) => this.onKeydown(e, i));
    });

    this.panels.forEach((panel) => {
      this.buildToc(panel);
      this.initGroups(panel);
    });

    this.selectFromHash();
    window.addEventListener('hashchange', () => this.selectFromHash());
  }

  disconnectedCallback() {
    if (this._onScroll) {
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onScroll);
    }
  }

  select(index, opts) {
    const options = opts || {};
    this.tabs.forEach((tab, i) => {
      const active = i === index;
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.setAttribute('tabindex', active ? '0' : '-1');
      tab.classList.toggle('is-active', active);
      if (active && options.focus) tab.focus();
    });

    this.panels.forEach((panel, i) => {
      const active = i === index;
      panel.hidden = !active;
      panel.classList.toggle('is-active', active);
    });

    // Keep the active tab visible when the list is scrolled on narrow screens.
    const tab = this.tabs[index];
    if (tab && this.tablist && this.tablist.scrollWidth > this.tablist.clientWidth) {
      const target = tab.offsetLeft - (this.tablist.clientWidth - tab.offsetWidth) / 2;
      this.tablist.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
    }

    if (options.updateHash !== false) {
      const slug = tab && tab.dataset.tabSlug;
      if (slug && window.history && window.history.replaceState) {
        window.history.replaceState(null, '', '#' + slug);
      }
    }
  }

  onKeydown(event, index) {
    const last = this.tabs.length - 1;
    let next = null;

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = index === last ? 0 : index + 1;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = index === 0 ? last : index - 1;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = last;
    else return;

    event.preventDefault();
    this.select(next, { focus: true });
  }

  selectFromHash() {
    const slug = (window.location.hash || '').replace('#', '');
    if (!slug) return;
    const index = this.tabs.findIndex((t) => t.dataset.tabSlug === slug);
    if (index === -1) return;
    // updateHash:false — the hash is already what we are reacting to.
    this.select(index, { focus: false, updateHash: false });
  }

  /*
   * Contents rail for one panel. Built from the DOM rather than printed in
   * Liquid so it can never list a topic that is not there — and so the
   * Ingredient Library's group filter can rebuild it by calling this again.
   */
  buildToc(panel) {
    const nav = panel.querySelector('[data-science-toc]');
    if (!nav) return;
    const aside = panel.querySelector('[data-science-aside]');

    const topics = Array.from(panel.querySelectorAll('[data-science-topic]')).filter(
      (topic) => !topic.hidden && topic.querySelector('.eltane-science-page__topic-title')
    );

    nav.textContent = '';

    // Under three entries the rail is longer than the thing it indexes.
    if (topics.length < 3) {
      if (aside) aside.hidden = true;
      return;
    }

    if (nav.dataset.scienceTocHideLabel !== 'true') {
      const label = document.createElement('p');
      label.className = 'eltane-science-page__toc-label';
      label.textContent = nav.dataset.scienceTocLabel || 'On this page';
      nav.appendChild(label);
    }

    const list = document.createElement('div');
    list.className = 'eltane-science-page__toc-list';
    nav.appendChild(list);

    topics.forEach((topic, i) => {
      if (!topic.id) topic.id = `${panel.id}-topic-${i + 1}`;

      const link = document.createElement('a');
      link.className = 'eltane-science-page__toc-link';
      link.href = `#${topic.id}`;
      link.dataset.scienceTocFor = topic.id;

      // Numbered to match the numerals hanging beside the headings themselves.
      // Both series count only topics that are actually rendered — this list is
      // pre-filtered on `hidden`, and the CSS counter driving the body skips
      // hidden topics because they generate no box — so the group filter cannot
      // push the two out of step.
      const num = document.createElement('span');
      num.className = 'eltane-science-page__toc-num';
      num.setAttribute('aria-hidden', 'true');
      num.textContent = String(i + 1).padStart(2, '0');
      link.appendChild(num);

      const text = document.createElement('span');
      text.className = 'eltane-science-page__toc-text';
      text.textContent = topic.querySelector('.eltane-science-page__topic-title').textContent.trim();
      link.appendChild(text);

      // Default anchor behaviour would rewrite the hash, and the hash is what
      // this component uses to remember which TAB is open — following a topic
      // link would silently reset the panel on the next load. Scroll by hand
      // and leave the hash alone. Offsetting is CSS's job (scroll-margin-top).
      link.addEventListener('click', (event) => {
        event.preventDefault();
        topic.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });

      list.appendChild(link);
    });

    if (aside) aside.hidden = false;
    this.observeTopics(topics);
  }

  observeTopics(topics) {
    if (!this._scrollBound) {
      this._scrollBound = true;
      let ticking = false;

      this._onScroll = () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(() => {
          ticking = false;
          this.markActiveTopic();
        });
      };

      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onScroll, { passive: true });
    }

    // Initial check
    this.markActiveTopic();
  }

  markActiveTopic() {
    const activePanel = this.querySelector('[role="tabpanel"]:not([hidden])');
    if (!activePanel) return;

    const visibleTopics = Array.from(activePanel.querySelectorAll('[data-science-topic]:not([hidden])'));
    if (!visibleTopics.length) return;

    // Use a third of the viewport as the natural reading trip-line, BUT ensure it is
    // always below the CSS scroll-margin-top. If the sticky header is taller than 33%
    // of the screen, the trip-line must sit below the header so clicked links register.
    const scrollMargin = parseInt(window.getComputedStyle(visibleTopics[0]).scrollMarginTop, 10);
    const line = Math.max(window.innerHeight * 0.33, (isNaN(scrollMargin) ? 0 : scrollMargin) + 20);

    let current = visibleTopics[0];

    for (let i = 0; i < visibleTopics.length; i++) {
      if (visibleTopics[i].getBoundingClientRect().top > line) break;
      current = visibleTopics[i];
    }

    // Hand it the rail once there is no scroll left.
    const atEnd = window.innerHeight + window.pageYOffset >= document.documentElement.scrollHeight - 2;
    if (atEnd) current = visibleTopics[visibleTopics.length - 1];

    this.querySelectorAll('[data-science-toc-for]').forEach((link) => {
      link.classList.toggle('is-active', link.dataset.scienceTocFor === current.id);
    });
  }

  initGroups(panel) {
    const controls = panel.querySelector('[data-science-groups]');
    if (!controls) return;
    const buttons = Array.from(controls.querySelectorAll('[data-science-group]'));
    if (!buttons.length) return;

    const apply = (group) => {
      buttons.forEach((b) => {
        const on = b.dataset.scienceGroup === group;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      panel.querySelectorAll('[data-science-in-group]').forEach((el) => {
        el.hidden = el.dataset.scienceInGroup !== group;
      });
      // The visible set changed, so the contents rail has to be rebuilt against
      // what is left rather than keep listing the other product's ingredients.
      this.buildToc(panel);
    };

    buttons.forEach((b) => b.addEventListener('click', () => apply(b.dataset.scienceGroup)));
    apply(buttons[0].dataset.scienceGroup);
  }
}

if (!customElements.get('eltane-science-tabs')) {
  customElements.define('eltane-science-tabs', EltaneScienceTabs);
}
